<template>
  <div>
    <Breadcrumbs main="Accounts" title="Trust Accounts" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">Trust Accounts</h5>
                <span class="text-muted">Client–matter trust accounts with current balances</span>
              </div>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  :disabled="loading"
                  @click="loadTrustAccounts"
                >
                  <vue-feather type="refresh-cw" class="me-1" />
                  Refresh
                </button>
                <button
                  v-if="canAdd"
                  class="btn btn-primary"
                  type="button"
                  @click="openCreateAccount"
                >
                  <vue-feather type="plus" class="me-1" />
                  Create Account
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="error" class="alert alert-danger" role="alert">
                {{ error }}
              </div>
              <div v-if="loading" class="text-center py-4 text-muted">
                Loading trust accounts...
              </div>
              <div v-else-if="accounts.length === 0" class="text-center py-4 text-muted">
                No trust accounts found. Create an account for a client and matter to get started.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>Account title</th>
                      <th>Client</th>
                      <th>Matter</th>
                      <th class="text-end">Balance</th>
                      <th>Created</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="account in accounts" :key="account.trustAccountId">
                      <td><strong>{{ account.accountTitle }}</strong></td>
                      <td>{{ clientName(account.clientId) }}</td>
                      <td>{{ matterLabel(account.matterId) }}</td>
                      <td class="text-end">
                        <strong>{{ formatMoney(account.balance) }}</strong>
                      </td>
                      <td>{{ formatDateTime(account.createdAt) }}</td>
                      <td class="text-end">
                        <button
                          v-if="canAdd"
                          class="btn btn-sm btn-outline-success me-1"
                          type="button"
                          title="Deposit"
                          @click="openDeposit(account)"
                        >
                          <vue-feather type="arrow-down-circle" class="me-1" />
                          Deposit
                        </button>
                        <button
                          v-if="canAdd"
                          class="btn btn-sm btn-outline-warning me-1"
                          type="button"
                          title="Withdraw"
                          @click="openWithdraw(account)"
                        >
                          <vue-feather type="arrow-up-circle" class="me-1" />
                          Withdraw
                        </button>
                        <button
                          class="btn btn-sm btn-outline-secondary me-1"
                          type="button"
                          title="View ledger"
                          @click="openLedger(account)"
                        >
                          <vue-feather type="list" />
                        </button>
                        <button
                          v-if="canDelete && Number(account.balance) === 0"
                          class="btn btn-sm btn-outline-danger"
                          type="button"
                          title="Delete account"
                          @click="confirmDeleteAccount(account)"
                        >
                          <vue-feather type="trash-2" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create account / Deposit / Withdraw -->
    <teleport to="body">
      <div
        v-if="showTransactionModal"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
          <div class="modal-content">
            <form @submit.prevent="submitTransaction">
              <div class="modal-header">
                <h5 class="modal-title">{{ transactionModalTitle }}</h5>
                <button type="button" class="btn-close" aria-label="Close" @click="closeTransactionModal" />
              </div>
              <div class="modal-body">
                <div v-if="formError" class="alert alert-danger" role="alert">
                  {{ formError }}
                </div>
                <div class="row">
                  <div v-if="formMode === 'createAccount'" class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Client <span class="text-danger">*</span></label>
                      <select
                        v-model.number="form.clientId"
                        class="form-select"
                        required
                        @change="onClientChange"
                      >
                        <option :value="null" disabled>Select a client</option>
                        <option
                          v-for="client in clients"
                          :key="client.clientId"
                          :value="client.clientId"
                        >
                          {{ client.fullName || `Client #${client.clientId}` }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div v-if="formMode === 'createAccount'" class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Matter <span class="text-danger">*</span></label>
                      <select
                        v-model.number="form.matterId"
                        class="form-select"
                        :disabled="!form.clientId"
                        required
                      >
                        <option :value="null" disabled>Select a matter</option>
                        <option
                          v-for="matter in selectableMatters"
                          :key="matter.matterId"
                          :value="matter.matterId"
                        >
                          {{ matterOptionLabel(matter) }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div v-if="formMode === 'createAccount'" class="col-12">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Account title <span class="text-danger">*</span></label>
                      <input
                        v-model="form.accountTitle"
                        class="form-control"
                        type="text"
                        maxlength="255"
                        required
                        placeholder="e.g. Smith v Jones - Retainer"
                      >
                    </div>
                  </div>
                  <div v-if="formMode === 'deposit'" class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Deposit amount <span class="text-danger">*</span></label>
                      <input
                        v-model.number="form.depositAmount"
                        class="form-control"
                        type="number"
                        min="0.01"
                        step="0.01"
                        required
                        placeholder="0.00"
                      >
                    </div>
                  </div>
                  <div v-if="formMode === 'withdraw'" class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Withdraw amount <span class="text-danger">*</span></label>
                      <input
                        v-model.number="form.withdrawAmount"
                        class="form-control"
                        type="number"
                        min="0.01"
                        step="0.01"
                        :max="selectedAccountBalance"
                        required
                        placeholder="0.00"
                      >
                      <small class="text-muted">
                        Available balance: {{ formatMoney(selectedAccountBalance) }}
                      </small>
                    </div>
                  </div>
                  <div v-if="formMode !== 'createAccount'" class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Transaction type</label>
                      <input
                        class="form-control"
                        type="text"
                        :value="form.transactionType"
                        readonly
                      >
                    </div>
                  </div>
                  <div v-if="formMode !== 'createAccount'" class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Reference no.</label>
                      <input
                        v-model="form.referenceNo"
                        class="form-control"
                        type="text"
                        maxlength="100"
                        placeholder="e.g. TRUST-2026-001"
                      >
                    </div>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeTransactionModal">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  {{ saving ? 'Saving...' : transactionSubmitLabel }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div v-if="showTransactionModal" class="modal-backdrop fade show" @click="closeTransactionModal" />
    </teleport>

    <!-- Ledger -->
    <teleport to="body">
      <div
        v-if="ledgerAccount"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <div>
                <h5 class="modal-title">Trust Ledger</h5>
                <span class="text-muted f-12">{{ ledgerAccount.accountTitle }}</span>
              </div>
              <button type="button" class="btn-close" aria-label="Close" @click="closeLedger" />
            </div>
            <div class="modal-body">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <span>
                  Current balance:
                  <strong>{{ formatMoney(ledgerAccount.balance) }}</strong>
                </span>
                <div v-if="canAdd" class="d-flex gap-2">
                  <button
                    class="btn btn-sm btn-outline-success"
                    type="button"
                    @click="openDeposit(ledgerAccount)"
                  >
                    Deposit
                  </button>
                  <button
                    class="btn btn-sm btn-outline-warning"
                    type="button"
                    @click="openWithdraw(ledgerAccount)"
                  >
                    Withdraw
                  </button>
                </div>
              </div>
              <div v-if="ledgerLoading" class="text-center py-3 text-muted">
                Loading transactions...
              </div>
              <div v-else-if="ledgerEntries.length === 0" class="text-center py-3 text-muted">
                No transactions recorded yet.
              </div>
              <div v-else class="table-responsive trust-ledger-table">
                <table class="table table-sm table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Type</th>
                      <th class="text-end">Deposit</th>
                      <th class="text-end">Withdraw</th>
                      <th class="text-end">Balance</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="entry in ledgerEntries" :key="entry.trustAccountTransactionId">
                      <td>{{ entry.referenceNo || `#${entry.trustAccountTransactionId}` }}</td>
                      <td>{{ entry.transactionType || '—' }}</td>
                      <td class="text-end">{{ formatMoney(entry.depositAmount) }}</td>
                      <td class="text-end">{{ formatMoney(entry.withdrawAmount) }}</td>
                      <td class="text-end">{{ formatMoney(entry.balance) }}</td>
                      <td>{{ formatLedgerDate(entry.createdAt) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeLedger">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="ledgerAccount" class="modal-backdrop fade show" @click="closeLedger" />
    </teleport>

    <!-- Delete account -->
    <teleport to="body">
      <div
        v-if="accountToDelete"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Delete Trust Account</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="accountToDelete = null" />
            </div>
            <div class="modal-body">
              <p class="mb-2">
                Delete trust account
                <strong>{{ accountToDelete.accountTitle }}</strong>?
              </p>
              <p class="text-muted mb-0 f-12">
                The account must have a zero balance. Transaction history is retained for audit.
              </p>
              <div v-if="actionError" class="alert alert-danger mt-3 mb-0" role="alert">
                {{ actionError }}
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="accountToDelete = null">
                Cancel
              </button>
              <button type="button" class="btn btn-danger" :disabled="actionLoading" @click="submitDeleteAccount">
                {{ actionLoading ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="accountToDelete" class="modal-backdrop fade show" @click="accountToDelete = null" />
    </teleport>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { listClients } from '@/services/clientService'
import { listMatters } from '@/services/matterService'
import {
  listTrustAccounts,
  createTrustAccount,
  depositTrustAccount,
  withdrawTrustAccount,
  listTrustAccountTransactions,
  deleteTrustAccount,
} from '@/services/trustAccountService'

function hasTrustAccountPrivilege(user, action) {
  if (!user?.privileges?.length) return true
  const priv = user.privileges.find((p) => p.entity === 'TRUST_ACCOUNT')
  if (!priv) return false
  const map = {
    view: 'canView',
    add: 'canAdd',
    edit: 'canEdit',
    delete: 'canDelete',
  }
  return priv[map[action]] === true
}

const emptyForm = () => ({
  trustAccountId: null,
  clientId: null,
  matterId: null,
  accountTitle: '',
  depositAmount: null,
  withdrawAmount: null,
  transactionType: '',
  referenceNo: '',
})

export default {
  name: 'AccountsTrustAccounts',

  computed: {
    ...mapGetters('auth', ['currentUser']),

    canAdd() {
      return hasTrustAccountPrivilege(this.currentUser, 'add')
    },

    canDelete() {
      return hasTrustAccountPrivilege(this.currentUser, 'delete')
    },

    existingClientMatterPairs() {
      return new Set(
        this.accounts.map((a) => `${a.clientId}:${a.matterId}`)
      )
    },

    selectableMatters() {
      if (!this.form.clientId) return []

      return this.matters.filter((m) => {
        if (m.clientId !== this.form.clientId) return false
        return !this.existingClientMatterPairs.has(`${m.clientId}:${m.matterId}`)
      })
    },

    selectedAccountBalance() {
      if (!this.form.trustAccountId) return 0
      const account = this.accounts.find((a) => a.trustAccountId === this.form.trustAccountId)
      return account ? Number(account.balance) || 0 : 0
    },

    transactionModalTitle() {
      if (this.formMode === 'createAccount') return 'Create Trust Account'
      if (this.formMode === 'deposit') return 'Deposit to Trust Account'
      return 'Withdraw from Trust Account'
    },

    transactionSubmitLabel() {
      if (this.formMode === 'createAccount') return 'Create Account'
      if (this.formMode === 'deposit') return 'Record Deposit'
      return 'Record Withdrawal'
    },

    ledgerEntries() {
      return [...this.ledgerTransactions].sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      )
    },
  },

  data() {
    return {
      accounts: [],
      matters: [],
      clients: [],
      loading: false,
      error: '',
      showTransactionModal: false,
      formMode: 'createAccount',
      form: emptyForm(),
      formError: '',
      saving: false,
      ledgerAccount: null,
      ledgerTransactions: [],
      ledgerLoading: false,
      accountToDelete: null,
      actionError: '',
      actionLoading: false,
    }
  },

  created() {
    this.loadTrustAccounts()
    this.loadMatters()
    this.loadClients()
  },

  methods: {
    async loadTrustAccounts() {
      this.loading = true
      this.error = ''

      try {
        const data = await listTrustAccounts()
        this.accounts = Array.isArray(data) ? data : []
        this.refreshLedgerAccount()
      } catch (err) {
        if (err.status === 403) {
          this.error = 'You do not have permission to view trust accounts.'
        } else {
          this.error = err.message || 'Failed to load trust accounts'
        }
      } finally {
        this.loading = false
      }
    },

    refreshLedgerAccount() {
      if (!this.ledgerAccount) return
      const updated = this.accounts.find(
        (a) => a.trustAccountId === this.ledgerAccount.trustAccountId
      )
      if (updated) {
        this.ledgerAccount = updated
        this.loadLedgerTransactions(updated.trustAccountId)
      } else {
        this.closeLedger()
      }
    },

    async loadMatters() {
      try {
        const data = await listMatters()
        this.matters = Array.isArray(data) ? data : []
      } catch {
        this.matters = []
      }
    },

    async loadClients() {
      try {
        const data = await listClients()
        this.clients = Array.isArray(data) ? data : []
      } catch {
        this.clients = []
      }
    },

    async loadLedgerTransactions(trustAccountId) {
      this.ledgerLoading = true

      try {
        const data = await listTrustAccountTransactions(trustAccountId)
        this.ledgerTransactions = Array.isArray(data) ? data : []
      } catch {
        this.ledgerTransactions = []
      } finally {
        this.ledgerLoading = false
      }
    },

    matterLabel(matterId) {
      const matter = this.matters.find((m) => m.matterId === matterId)
      if (!matter) return `#${matterId}`
      return this.matterOptionLabel(matter)
    },

    matterOptionLabel(matter) {
      const parts = []
      if (matter.caseNo) parts.push(matter.caseNo)
      if (matter.caseTitle) parts.push(matter.caseTitle)
      if (parts.length === 0) return `Matter #${matter.matterId}`
      return parts.join(' — ')
    },

    clientName(clientId) {
      if (!clientId) return '—'
      const client = this.clients.find((c) => c.clientId === clientId)
      return client?.fullName || `#${clientId}`
    },

    formatDateTime(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString()
    },

    formatLedgerDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString(undefined, {
        year: '2-digit',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    formatMoney(value) {
      if (value == null || Number.isNaN(Number(value))) return '—'
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    openCreateAccount() {
      this.formMode = 'createAccount'
      this.form = emptyForm()
      this.formError = ''
      this.showTransactionModal = true
    },

    onClientChange() {
      this.form.matterId = null
    },

    openDeposit(account) {
      this.formMode = 'deposit'
      this.form = {
        trustAccountId: account.trustAccountId,
        clientId: null,
        matterId: null,
        accountTitle: '',
        depositAmount: null,
        withdrawAmount: null,
        transactionType: 'CLIENT_DEPOSIT',
        referenceNo: '',
      }
      this.formError = ''
      this.showTransactionModal = true
    },

    openWithdraw(account) {
      this.formMode = 'withdraw'
      this.form = {
        trustAccountId: account.trustAccountId,
        clientId: null,
        matterId: null,
        accountTitle: '',
        depositAmount: null,
        withdrawAmount: null,
        transactionType: 'DISBURSEMENT',
        referenceNo: '',
      }
      this.formError = ''
      this.showTransactionModal = true
    },

    async openLedger(account) {
      this.ledgerAccount = account
      await this.loadLedgerTransactions(account.trustAccountId)
    },

    closeLedger() {
      this.ledgerAccount = null
      this.ledgerTransactions = []
    },

    closeTransactionModal() {
      if (this.saving) return
      this.showTransactionModal = false
      this.formError = ''
    },

    validateTransaction() {
      if (this.formMode === 'createAccount') {
        if (!this.form.clientId) return 'Please select a client.'
        if (!this.form.matterId) return 'Please select a matter.'
        const title = this.form.accountTitle?.trim()
        if (!title) return 'Account title is required.'
        if (title.length > 255) return 'Account title must not exceed 255 characters.'
        return ''
      }

      if (!this.form.trustAccountId) {
        return 'Trust account is required.'
      }

      if (this.formMode === 'deposit') {
        const deposit = Number(this.form.depositAmount)
        if (!Number.isFinite(deposit) || deposit <= 0) {
          return 'Enter a deposit amount greater than zero.'
        }
        return ''
      }

      const withdraw = Number(this.form.withdrawAmount)
      if (!Number.isFinite(withdraw) || withdraw <= 0) {
        return 'Enter a withdrawal amount greater than zero.'
      }
      if (withdraw > this.selectedAccountBalance) {
        return 'Withdrawal amount exceeds the available balance.'
      }

      return ''
    },

    buildCreatePayload() {
      return {
        clientId: this.form.clientId,
        matterId: this.form.matterId,
        accountTitle: this.form.accountTitle.trim(),
      }
    },

    buildDepositPayload() {
      const payload = {
        trustAccountId: this.form.trustAccountId,
        amount: Number(this.form.depositAmount),
      }

      const transactionType = this.form.transactionType?.trim()
      if (transactionType) payload.transactionType = transactionType

      const referenceNo = this.form.referenceNo?.trim()
      if (referenceNo) payload.referenceNo = referenceNo

      return payload
    },

    buildWithdrawPayload() {
      const payload = {
        trustAccountId: this.form.trustAccountId,
        amount: Number(this.form.withdrawAmount),
      }

      const transactionType = this.form.transactionType?.trim()
      if (transactionType) payload.transactionType = transactionType

      const referenceNo = this.form.referenceNo?.trim()
      if (referenceNo) payload.referenceNo = referenceNo

      return payload
    },

    async submitTransaction() {
      this.formError = ''

      const validationError = this.validateTransaction()
      if (validationError) {
        this.formError = validationError
        return
      }

      this.saving = true

      try {
        if (this.formMode === 'createAccount') {
          await createTrustAccount(this.buildCreatePayload())
        } else if (this.formMode === 'deposit') {
          await depositTrustAccount(this.buildDepositPayload())
        } else {
          await withdrawTrustAccount(this.buildWithdrawPayload())
        }

        this.showTransactionModal = false
        await this.loadTrustAccounts()
      } catch (err) {
        this.formError = err.message || 'Request failed'
      } finally {
        this.saving = false
      }
    },

    confirmDeleteAccount(account) {
      this.accountToDelete = account
      this.actionError = ''
    },

    async submitDeleteAccount() {
      this.actionError = ''
      this.actionLoading = true

      try {
        await deleteTrustAccount(this.accountToDelete.trustAccountId)
        this.accountToDelete = null
        await this.loadTrustAccounts()
      } catch (err) {
        this.actionError = err.message || 'Failed to delete trust account'
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>

<style scoped>
.trust-ledger-table .table {
  font-size: 0.8125rem;
}

.trust-ledger-table .table th,
.trust-ledger-table .table td {
  padding: 0.2rem 0.45rem;
  white-space: nowrap;
}
</style>
